import moment from 'moment';
import { generatorTooltipText } from '@/vue/helpers/tooltipBulletPoint.js';

const parseJSON = value => {
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

/**
 * Utility class to create a custom HTML marker for mapbox using HTML.
 */
export default class HtmlInstallationPopup {
    constructor(
        options = {
            installations: [],
            onClick: undefined,
        },
    ) {
        this.installations = options.installations;
        if (this.installations.length === 1) {
            // the onClick the e.features transforms the lists/objects into a string in layerIdIndividual (single pins on the map)
            this.installations[0].tanks = parseJSON(this.installations[0].tanks);
            this.installations[0].current_alarms_formatted = parseJSON(
                this.installations[0].current_alarms_formatted,
            );
        }
        this.onClick = options.onClick;

        this.onToggleAccordion = installationId => {
            const elementAccordion = document.getElementById(`map-${installationId}-accordion`);
            elementAccordion.classList.toggle('accordion-open');

            // Rotate arrow
            const elementArrow = document.getElementById(`map-${installationId}-arrow`);
            elementArrow.classList.toggle('rotate-arrow');
        };

        if (this.installations.length > 1) {
            // If there are more than 1 installation, sort the list based on alarm and warnings.
            this.installations.sort(this._sort);
        }

        this.tiles = this.installations.map(
            i =>
                new HtmlInstallationPopupTile({
                    installation: i,
                    onClick: this.onClick,
                    onToggleAccordion: this.onToggleAccordion,
                }),
        );
    }

    /**
     * Sort installation tiles based on their alarm and warning status.
     *
     * Alarms are shown first, then warnings, then all OK installations.
     *
     * @param a - Installation A
     * @param b - Installation B
     * @returns {number}
     * @private
     */
    _sort(a, b) {
        if (a.status === b.status) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        } else if (a.status === 'alarm') {
            return -1;
        } else if (b.status === 'alarm') {
            return 1;
        } else if (a.status === 'warning') {
            return -1;
        } else if (b.status === 'warning') {
            return 1;
        }

        return 0;
    }

    /**
     * Create an HTML ChildNode element from the basic HTML string.
     *
     * Needed for use within Mapbox.
     *
     * @returns {ChildNode}
     */
    buildChildNode() {
        let root = document.createElement('div.map-popup__content');

        this.tiles.forEach(tile => root.appendChild(tile.buildChildNode()));

        let el = document.createElement('div');
        el.appendChild(root);
        return el.firstChild;
    }
}

class HtmlInstallationPopupTile {
    constructor(
        options = {
            installation: {},
            onClick: undefined,
            onToggleAccordion: undefined,
        },
    ) {
        this.installation = options.installation;
        this.onClick = options.onClick;
        this.onToggleAccordion = options.onToggleAccordion;
        this.expandable = false;

        this.maxCharacters =
            parseInt(
                getComputedStyle(document.body).getPropertyValue(
                    '--map-popup-installation-tanks-max-characters',
                ),
            ) || 50;
    }

    /**
     * Whether the installation's data is expandable and accordion is needed.
     * @type {boolean}
     */
    expandable;

    /**
     * Name of the installation.
     *
     * @returns {string}
     */
    get name() {
        return this.installation.name;
    }

    /**
     * Time ago string of the installations last update.
     *
     * @returns {string}
     */
    get lastUpdate() {
        return moment(this.installation.lastTimestamp * 1000).fromNow();
    }

    /**
     * Title of the energy source the installation is running on.
     *
     * @returns {string}
     */
    get runningOn() {
        return (
            this.installation.runningOn.charAt(0).toUpperCase() +
            this.installation.runningOn.slice(1)
        );
    }

    /**
     * Status of the installation.
     *
     * @returns {string}
     */
    get status() {
        if (this.classNames === 'alarm') {
            return 'Alarm';
        }
        if (this.classNames === 'warning') {
            return 'Warning';
        }
        return 'OK';
    }

    get alarmMessageLength() {
        return this.installation.current_alarms_formatted.length;
    }

    get statusIcon() {
        switch (this.classNames) {
            case 'alarm':
                return '<i class="sg-vrm-icon_126_alarm-16px"></i>';
            case 'warning':
                return '<i class="sg-vrm-icon_124_warning-16px"></i>';
            default:
                return '<i class="sg-vrm-icon_125_ok-16px"></i>';
        }
    }

    get classNames() {
        if (this.installation.alarm) {
            return 'alarm';
        }

        if (this.installation.status === 'warning') {
            return 'warning';
        }

        return '';
    }

    get tanks() {
        return this.installation.tanks;
    }

    get hasTankLevel() {
        return Object.keys(this.installation.tanks || {}).length > 0;
    }

    get serviceCounter() {
        if (this.installation.serviceInterval <= 0) {
            return undefined;
        }

        return this.installation.serviceCountDown;
    }

    get hasServiceCounter() {
        return this.serviceCounter !== undefined && this.serviceCounter !== null;
    }

    get serviceWarning() {
        return this.installation.serviceWarning;
    }

    get alarmMessagesBulletPoint() {
        if (!this.alarmMessageLength) {
            return '';
        }

        const alarms = this.installation.current_alarms_formatted
            .map(alarm => {
                return generatorTooltipText(alarm, 'red');
            })
            .join('');
        return `<ul>${alarms}</ul>`;
    }

    buildHtml() {
        let html = `<div class="map-popup__installation">`;
        // Add title info
        html += `<div class="map-popup__installation__title"><span>${this.name}</span></div>`;
        html += `<div class="map-popup__installation__subtitle status wrap"
                >Status:&nbsp<span>${this.statusIcon}${this.status}</span>`;
        if (this.classNames === 'alarm') {
            this.expandable = true;
            html += `<span class="divider">|</span>
                    <span class="alarm-message">${this.alarmMessageLength} alarm(s)</span>`;
            html += `<div class="bullet-point map-accordion-on-open">${this.alarmMessagesBulletPoint}</div>`;
        }
        html += `</div>`; // close status div
        html += `<div class="map-popup__installation__subtitle">Last connection: <span>${this.lastUpdate}</span></div>`;
        html += `<div class="map-popup__installation__subtitle">Running on: <span>${this.runningOn}</span></div>`;

        // If the installation has tank or service stats, add them.
        if (this.hasTankLevel || this.hasServiceCounter) {
            html += `<div class="map-popup__stats">`;

            if (this.hasServiceCounter) {
                html += this.buildPopupStat(
                    'service',
                    `${this.serviceCounter} hr`,
                    this.serviceWarning ? 'warning' : '',
                    this.serviceWarning,
                );
            }

            if (this.hasTankLevel) {
                const { previewHtml, fullHtml, expandable } = this.buildTankLevels();
                if (expandable) {
                    this.expandable = true;
                    html += `<div class="map-popup__stats__tanks map-accordion-on-close">${previewHtml}</div>`;
                    html += `<div class="map-popup__stats__tanks map-accordion-on-open">${fullHtml}</div>`;
                } else {
                    html += `<div class="map-popup__stats__tanks">${previewHtml}</div>`;
                }
            }

            // End add stats
            html += '</div>';
        }

        html += '</div>';

        return html;
    }

    /**
     * Build the tank levels for the installation.
     * @returns {{previewHtml: string, fullHtml: string, expandable: boolean}}
     * @property {string} previewHtml - The HTML for the preview (when not expanded).
     * @property {string} fullHtml - The full HTML (when expanded).
     * @property {boolean} expandable - Whether the text is long enough to be expandable.
     */
    buildTankLevels() {
        let previewRendered = false;
        let charCount = 0;
        let previewHtml = '';
        let fullHtml = '';

        const appendToPreview = html => {
            if (previewRendered) return;
            if (charCount > this.maxCharacters) {
                previewHtml += ' ...';
                previewRendered = true;
                return;
            }
            previewHtml += html;
        };

        const appendToBoth = html => {
            fullHtml += html;
            appendToPreview(html);
        };

        Object.keys(this.tanks).forEach((tankGroup, tankGroupIndex) => {
            fullHtml += '<div class="map-popup__stat__tank-type">';
            if (tankGroupIndex > 0) {
                appendToPreview(`<span class="map-popup__stat__divider"></span>`);
            }
            charCount += tankGroup.length + 1;
            appendToBoth(`${tankGroup} `);
            const tankGroupLastItemIndex = this.tanks[tankGroup].length - 1;
            this.tanks[tankGroup].forEach((tank, tankIndex) => {
                const warning = tank.warning ? 'warning' : '';
                charCount += tank.level.toString().length + 4; // 4 for the %, space, and margins
                appendToBoth(
                    `<span class="map-popup__stat__label ${warning}">${tank.level} %</span>`,
                );
                // If not the last tank in the group
                if (tankIndex < tankGroupLastItemIndex) {
                    appendToBoth(`<span class="map-popup__stat__divider"></span>`);
                }
            });
            fullHtml += '</div>';
        });
        return { previewHtml, fullHtml, expandable: previewRendered };
    }

    buildPopupStat(type, value, classes = '', hasWarning) {
        let html = `<div class="map-popup__stat ${type} ${classes}">`;

        html += `<div class="map-popup__stat__block"></div>`;
        html += `<div class="map-popup__stat__label">${value}</div>`;

        if (hasWarning) {
            html += `<span class="map-stat__exclamation sg-vrm-icon_127_map_warning-16px"></span>`;
        }

        html += '</div>';
        return html;
    }

    /**
     * Create an HTML ChildNode element from the basic HTML string.
     *
     * Needed for use within Mapbox.
     *
     * @returns {ChildNode}
     */
    buildChildNode() {
        let installationElement = document.createElement('div');
        installationElement.className = `map-popup__row ${this.classNames}`;
        installationElement.id = `map-${this.installation.idSite}-accordion`;

        let informationElement = document.createElement('div');
        informationElement.innerHTML = this.buildHtml();

        let child = informationElement.firstChild;
        if (this.onClick) {
            child.addEventListener('click', () => {
                this.onClick(this.installation);
            });
        }
        installationElement.appendChild(child);

        if (this.expandable) {
            let arrowElement = document.createElement('div');
            arrowElement.innerHTML = `<i id="map-${this.installation.idSite}-arrow" class="sg-vrm-icon_051_arrow-down-16px"></i>`;
            arrowElement.className = 'map-popup__arrow';
            arrowElement.addEventListener('click', () => {
                this.onToggleAccordion(this.installation.idSite);
            });
            installationElement.appendChild(arrowElement);
        }
        return installationElement;
    }
}
